import {
    Grid,
    IconButton,
    Tooltip,
    Button,
    Box,
    Typography,
} from '@mui/material';
import FieldText from '../../../../components/general/fields/fieldText';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormikProps } from 'formik';
import { MultiDynamicField } from '../../../../../shared/types/assistant';

interface MultiFieldDynamicFieldsProps {
    formik: FormikProps<{ [key: string]: MultiDynamicField[] }> | undefined;
    name: string;
    label?: string;
    description?: string;
    example?: {
        title: string;
        description: string;
    };
}

export const MultiFieldDynamicFields = ({
    formik,
    name,
    label,
    description,
    example,
}: MultiFieldDynamicFieldsProps) => {
    const fieldValues = formik?.values[name] || [];

    const handleAddField = () => {
        formik?.setFieldValue(name, [
            ...fieldValues,
            { title: '', description: '' },
        ]);
    };

    const handleRemoveField = (index: number) => {
        formik?.setFieldValue(
            name,
            fieldValues.filter((_, i) => i !== index)
        );
    };

    const handleFieldChange = (
        index: number,
        field: 'title' | 'description',
        value: string
    ) => {
        const updatedFields = [...fieldValues];
        updatedFields[index][field] = value;
        formik?.setFieldValue(name, updatedFields);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {label}
            </Typography>

            {description && (
                <Typography color="text.secondary">{description}</Typography>
            )}

            {example && (
                <Box mt={2}>
                    <Typography color="text.secondary">
                        <strong>Ejemplo:</strong>
                    </Typography>
                    <Typography color="text.secondary">
                        <strong>{example.title}:</strong> {example.description}
                    </Typography>
                </Box>
            )}

            <Box mt={2} display="flex" justifyContent="start">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddField}
                >
                    Agregar otra
                </Button>
            </Box>

            {fieldValues.map((fieldItem, index) => (
                <Grid container spacing={2} key={index} pt="20px">
                    <Grid item xs={12} sm={4.5} md={3}>
                        <FieldText
                            label="Título"
                            name={`${name}[${index}].title`}
                            size="small"
                            required
                            onBlur={formik?.handleBlur}
                            touched={formik?.touched?.[name]?.[index]?.title}
                            errors={
                                (
                                    formik?.errors?.[name]?.[
                                        index
                                    ] as MultiDynamicField
                                )?.title
                            }
                            value={fieldItem.title}
                            onChange={(e) =>
                                handleFieldChange(
                                    index,
                                    'title',
                                    e.target.value
                                )
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6.5} md={8.5}>
                        <FieldText
                            label="Descripción"
                            name={`${name}[${index}].description`}
                            size="small"
                            required
                            onBlur={formik?.handleBlur}
                            touched={
                                formik?.touched?.[name]?.[index]?.description
                            }
                            errors={
                                (
                                    formik?.errors?.[name]?.[
                                        index
                                    ] as MultiDynamicField
                                )?.description
                            }
                            value={fieldItem.description}
                            onChange={(e) =>
                                handleFieldChange(
                                    index,
                                    'description',
                                    e.target.value
                                )
                            }
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={1}
                        md={0.5}
                        display="flex"
                        alignItems="center"
                        justifyContent="end"
                    >
                        <Tooltip title="Eliminar">
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveField(index)}
                            >
                                <DeleteIcon sx={{ color: 'error.main' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            ))}
        </Box>
    );
};
